import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ExternalLink, ChevronDown, ChevronUp, CheckCircle, AlertTriangle, Info, ArrowRight, Ticket, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useLanguage } from '../contexts/LanguageContext';
import { API } from '../App';

const PlatformGuide = ({ platform, isOpen, onToggle }) => {
  if (!platform) return null;

  const getNoteClass = (type) => {
    switch(type) {
      case 'success': return 'bg-green-50 border-green-200 text-green-800';
      case 'warning': return 'bg-amber-50 border-amber-200 text-amber-800';
      case 'info': return 'bg-blue-50 border-blue-200 text-blue-800';
      default: return 'bg-gray-50 border-gray-200 text-gray-800';
    }
  };

  const getNoteIcon = (type) => {
    switch(type) {
      case 'success': return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'warning': return <AlertTriangle className="h-4 w-4 text-amber-600" />;
      case 'info': return <Info className="h-4 w-4 text-blue-600" />;
      default: return <Info className="h-4 w-4 text-gray-600" />;
    }
  };

  return (
    <div className="bg-white rounded-xl overflow-hidden shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
      <button
        onClick={onToggle}
        className="w-full px-4 md:px-6 py-4 md:py-5 text-left flex items-center justify-between gap-4 hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <span className="text-2xl">{platform.icon}</span>
          <div>
            <h3 className="font-semibold text-gray-900 text-base md:text-lg">{platform.display_name || platform.name}</h3>
            <p className="text-sm text-gray-500">{platform.transfer_type}</p>
          </div>
        </div>
        {isOpen ? (
          <ChevronUp className="h-5 w-5 text-[#2eb382] flex-shrink-0" />
        ) : (
          <ChevronDown className="h-5 w-5 text-gray-400 flex-shrink-0" />
        )}
      </button>
      
      {isOpen && (
        <div className="px-4 md:px-6 pb-5 border-t border-gray-100">
          <div className="pt-4 space-y-4">
            {/* Login Button */}
            {platform.login_url && (
              <a
                href={platform.login_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#67ffbb] hover:bg-[#4de6a8] text-gray-900 font-semibold rounded-lg transition-colors"
              >
                <ExternalLink className="h-4 w-4" />
                Se connecter à {platform.display_name || platform.name}
              </a>
            )}

            {/* Steps */}
            {platform.steps && platform.steps.length > 0 && (
              <div>
                <h4 className="font-semibold text-gray-800 mb-3">Étapes de transfert :</h4>
                <ol className="space-y-2">
                  {platform.steps.map((step, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <span className="w-6 h-6 bg-[#67ffbb]/30 text-[#2eb382] rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0">
                        {index + 1}
                      </span>
                      <span className="text-gray-700 text-sm md:text-base">{step}</span>
                    </li>
                  ))}
                </ol>
              </div>
            )}

            {/* After Transfer */}
            {platform.after_transfer && platform.after_transfer.length > 0 && (
              <div>
                <h4 className="font-semibold text-gray-800 mb-3">Ce qui se passe ensuite :</h4>
                <ul className="space-y-2">
                  {platform.after_transfer.map((item, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-[#2eb382] mt-0.5 flex-shrink-0" />
                      <span className="text-gray-600 text-sm">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Note */}
            {platform.note && (
              <div className={`flex items-start gap-2 p-3 rounded-lg border ${getNoteClass(platform.note_type)}`}>
                {getNoteIcon(platform.note_type)}
                <span className="text-sm font-medium">{platform.note}</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const PlatformGuidesPage = ({ user, onLogout, refreshUserData }) => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const [openPlatform, setOpenPlatform] = useState(null);
  const [platforms, setPlatforms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPlatforms = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API}/platforms`);
        setPlatforms(response.data || []);
      } catch (err) {
        console.error('Error fetching platforms:', err);
        setError('Impossible de charger les plateformes');
      } finally {
        setLoading(false);
      }
    };
    fetchPlatforms();
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 font-['Open_Sans']">
      <Navbar user={user} onLogout={onLogout} refreshUserData={refreshUserData} />
      
      {/* Spacer for fixed navbar */}
      <div className="h-16 md:h-20"></div>

      {/* Header */}
      <div className="bg-gradient-to-r from-gray-900 to-gray-800 text-white py-10 md:py-16">
        <div className="container mx-auto px-4 text-center">
          <Ticket className="h-12 w-12 mx-auto mb-4 text-[#67ffbb]" />
          <h1 className="text-2xl md:text-4xl lg:text-5xl font-bold mb-3 md:mb-4">
            {language === 'fr' ? 'Guide des plateformes de billets' : 'Ticket Platform Guides'}
          </h1>
          <p className="text-base md:text-xl text-gray-300 max-w-2xl mx-auto">
            {language === 'fr' 
              ? 'Apprenez comment transférer vos billets sur chaque plateforme'
              : 'Learn how to transfer your tickets on each platform'}
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="py-8 md:py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            {/* Intro */}
            <div className="bg-[#67ffbb]/10 border border-[#67ffbb]/30 rounded-xl p-4 md:p-6 mb-8">
              <div className="flex items-start gap-3">
                <Info className="h-5 w-5 text-[#2eb382] mt-0.5 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">
                    {language === 'fr' ? 'Comment utiliser ces guides' : 'How to use these guides'}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {language === 'fr' 
                      ? 'Cliquez sur une plateforme pour voir les étapes détaillées du transfert. Utilisez le bouton de connexion pour accéder directement à votre compte sur la plateforme.'
                      : 'Click on a platform to see detailed transfer steps. Use the login button to access your account directly on the platform.'}
                  </p>
                </div>
              </div>
            </div>

            {/* Platform List */}
            <div className="space-y-3">
              {loading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-[#2eb382]" />
                </div>
              ) : error ? (
                <div className="text-center text-red-600 py-8">{error}</div>
              ) : platforms.length === 0 ? (
                <div className="text-center text-gray-500 py-8">
                  Aucune plateforme disponible
                </div>
              ) : (
                platforms.map((platform) => (
                  <PlatformGuide
                    key={platform.id || platform.name}
                    platform={platform}
                    isOpen={openPlatform === (platform.id || platform.name)}
                    onToggle={() => setOpenPlatform(openPlatform === (platform.id || platform.name) ? null : (platform.id || platform.name))}
                  />
                ))
              )}
            </div>

            {/* Missing Platform Note */}
            <div className="mt-8 text-center">
              <p className="text-gray-500 text-sm mb-3">
                {language === 'fr' 
                  ? 'Votre plateforme n\'est pas listée?'
                  : 'Your platform is not listed?'}
              </p>
              <Button
                onClick={() => navigate('/contact?category=nouvelle_billeterie')}
                variant="outline"
                className="border-[#67ffbb] text-[#2eb382] hover:bg-[#67ffbb]/10"
              >
                {language === 'fr' ? 'Suggérer une plateforme' : 'Suggest a platform'}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-gradient-to-r from-gray-800 to-gray-900 text-white py-12 md:py-16">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-xl md:text-3xl font-bold mb-3 md:mb-4">
            {language === 'fr' ? 'Prêt à transférer vos billets?' : 'Ready to transfer your tickets?'}
          </h2>
          <p className="text-gray-300 mb-6 md:mb-8 max-w-2xl mx-auto text-sm md:text-base">
            {language === 'fr' 
              ? 'Utilisez Middleman pour sécuriser votre transaction et protéger acheteur et vendeur.'
              : 'Use Middleman to secure your transaction and protect both buyer and seller.'
            }
          </p>
          <Button 
            onClick={() => navigate('/auth')}
            className="bg-[#67ffbb] hover:bg-[#4de6a8] text-gray-900 font-semibold px-8 py-3"
          >
            {language === 'fr' ? 'Commencer un transfert' : 'Start a transfer'}
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default PlatformGuidesPage;
